import { ContentBlock, ContentState, convertToRaw, EditorState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import * as R from "ramda";
import * as React from "react";
import { Editor } from "react-draft-wysiwyg";
import { FaCode, FaRegSave } from "react-icons/fa";
import { config } from "../../../config";
import { PreferredLocale } from "../../../utils/locales";
import { getImageComponent } from "./custom/Image.component";
import { ICustomBlock, IEditorHelpers } from "./custom/types";
import { prependParagraph, updateCenterAlignedImages, updateEmptyParagraphs, updateImages } from "./Editor.helpers";
import { ButtonsContainer, Container, EditRawButton, SubmitButton } from "./Editor.styles";
import { RawContentEditor } from "./RawContentEditor.component";
import { IHTMLEditorProps, IHTMLEditorState, IUploadedImgResponse } from "./types";

const sanitizeHTML = prependParagraph;

export class HTMLEditorComponent extends React.Component<IHTMLEditorProps, IHTMLEditorState> {
  private toolbarSettings: object;
  private editorHelpers: IEditorHelpers;
  private editorRef = React.createRef<Editor>();
  constructor(props: IHTMLEditorProps) {
    super(props);
    this.state = this.initState(props);
    this.editorHelpers = {
      getEditorState: this.getEditorState,
      onChange: this.editContent,
    };
    this.toolbarSettings = {
      embedded: {
        defaultSize: {
          height: "486",
          width: "100%",
        },
      },
      image: {
        alignmentEnabled: true,
        alt: {
          mandatory: false,
          present: true,
        },
        inputAccept: "image/gif,image/jpeg,image/jpg,image/png,image/svg",
        previewImage: true,
        uploadCallback: this.uploadImg,
        uploadEnabled: true,
        urlEnabled: true,
      },
    };
  }

  public initState(props: IHTMLEditorProps): IHTMLEditorState {
    const initialContent = R.isNil(props.initialContent) ? this.getContent() : this.getContent(props.initialContent);
    const content = htmlToDraft(initialContent);
    if (R.isNil(content)) {
      return {
        editorState: EditorState.createEmpty(),
        isEditing: false,
        rawContent: {
          editing: false,
          value: "",
        },
        readOnly: false,
      };
    }
    const contentState = ContentState.createFromBlockArray(content.contentBlocks);
    return {
      editorState: EditorState.createWithContent(contentState),
      isEditing: false,
      rawContent: {
        editing: false,
        value: initialContent,
      },
      readOnly: false,
    };
  }

  public componentDidMount() {
    if (this.props.shouldAutoFocus) {
      this.focusEditor();
    }
    document.addEventListener("mousedown", this.checkIfClickedOutside);
  }

  public componentDidUpdate(oldProps: IHTMLEditorProps) {
    const { shouldAutoSave, shouldResetState } = this.props;
    if (shouldAutoSave && shouldAutoSave(this.props, oldProps)) {
      // Save current content before updating state with new content
      // Only works if currentLocale prop is defined
      this.submitContent(oldProps.currentLocale)();
    }
    if (shouldResetState && shouldResetState(this.props, oldProps)) {
      this.setState(this.initState(this.props));
    }
  }

  public componentWillUnmount(): void {
    document.removeEventListener("mousedown", this.checkIfClickedOutside);
    document.removeEventListener("keydown", this.handleKeyPress);
  }

  public focusEditor = () => {
    this.editorRef.current && this.editorRef.current.focusEditor();
    this.setState({ isEditing: true });
    document.addEventListener("keydown", this.handleKeyPress);
  };

  public editContent = (editorState: EditorState) => {
    const { rawContent } = this.state;
    const { editing } = rawContent;
    const readOnly = false;
    const value = this.getRawFromEditorState(editorState);
    if (editing) {
      this.setState({
        editorState,
        rawContent: { ...rawContent, value },
        readOnly,
      });
    } else {
      this.setState({ editorState, readOnly });
    }

    if (this.props.onContentChange) {
      this.props.onContentChange(value);
    }
  };

  public editRawContent = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { rawContent } = this.state;
    const value = event.target.value;
    const editorState = this.getEditorStateFromRaw(value);
    this.setState({ editorState, rawContent: { ...rawContent, value }, isEditing: true });
  };

  public submitContent = (currentLocale?: PreferredLocale) => () => {
    const content = this.getRawFromEditorState(this.state.editorState);
    this.setContent(content, currentLocale);
    this.setState({ isEditing: false });
  };

  public autoSave = () => {
    const content = this.getRawFromEditorState(this.state.editorState);
    this.setContent(content);
    this.submitContent(this.props.currentLocale);
  };

  public checkIfClickedOutside = (e: Event) => {
    const clickedOutside = !this.props.containerRef.current?.contains(e.target as HTMLElement);
    if (clickedOutside) {
      if (this.state.isEditing) {
        this.autoSave();
        document.removeEventListener("keydown", this.handleKeyPress);
      }
      this.setState({ isEditing: false });
      this.props.toggleEdit?.();
    } else {
      document.addEventListener("keydown", this.handleKeyPress);
      this.setState({ isEditing: true });
    }
  };

  public handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      this.autoSave();
    }
  };

  public render() {
    const toolbarCustomButtons = this.props.toolbarCustomButtons || [];
    const { rawContent, readOnly } = this.state;
    const { editing } = rawContent;
    return (
      <Container data-testid="editor">
        <Editor
          ref={this.editorRef}
          editorState={this.state.editorState}
          toolbar={this.toolbarSettings}
          editorStyle={{ padding: "0.5rem" }}
          onEditorStateChange={this.editContent}
          toolbarCustomButtons={toolbarCustomButtons}
          customBlockRenderFunc={this.blockRenderFunction}
          readOnly={readOnly}
        />
        <RawContentEditor {...{ rawContent, onChange: this.editRawContent }} />
        <ButtonsContainer>
          <SubmitButton onClick={this.submitContent(this.props.currentLocale)}>
            <FaRegSave />
            Submit
          </SubmitButton>
          <EditRawButton onClick={this.toggleEditRaw} selected={editing}>
            <FaCode />
            {editing ? "Stop editing" : "Edit"} raw HTML
          </EditRawButton>
        </ButtonsContainer>
      </Container>
    );
  }

  public toggleEditRaw = () => {
    const { rawContent, editorState } = this.state;
    const { editing } = rawContent;
    if (!editing) {
      const value: string = this.getRawFromEditorState(editorState);
      this.setState({ rawContent: { editing: true, value }, isEditing: true });
    } else {
      this.setState({ rawContent: { ...rawContent, editing: false }, isEditing: false });
    }
  };

  public getFormatedEditorContent = (
    content: string = this.getRawFromEditorState(this.state.editorState),
    currentLocale?: PreferredLocale
  ) => {
    // Backend requires that HTML content be wrapped in an extra div, so we
    // check for one and add if it is missing
    const original = content.trim();
    const length = content.length;
    if (original.slice(0, 5) === "<div>" && original.slice(length - 6, length) === "</div>") {
      return original;
    }
    return `<div>${original}</div>`;
  };

  public getRawFromEditorState = (editorState: EditorState): string =>
    R.pipe(
      convertToRaw,
      draftToHtml,
      updateEmptyParagraphs,
      updateImages,
      updateCenterAlignedImages
    )(editorState.getCurrentContent());

  private setContent = (content: string, currentLocale?: PreferredLocale) => {
    this.props.setContent(this.getFormatedEditorContent(content, currentLocale), currentLocale);
  };

  private getContent = (initial?: string) => {
    // Backend may have wrapped HTML content with an extra div, so we check
    // for one and remove it
    const original = (!R.isNil(initial) ? initial : this.props.getContent()).trim();
    const length = original.length;
    if (original.slice(0, 5) === "<div>" && original.slice(length - 6, length) === "</div>") {
      return original.slice(5, length - 6);
    }
    return original;
  };

  private uploadImg = (file: File): Promise<IUploadedImgResponse> => {
    return new Promise((resolve, reject) =>
      this.props.uploadFile(file).then(([error, result]) => {
        if (error) {
          return reject(error);
        }

        if (result) {
          const link = `${config.saltcomplianceApiRoot}/images/${result.data[0].mediaId.slice(4)}/_download`;
          return resolve({
            data: { link },
          });
        }
      })
    );
  };

  private getEditorStateFromRaw = (rawContent: string): EditorState => {
    const content = htmlToDraft(sanitizeHTML(this.getContent(rawContent)));
    return R.isNil(content)
      ? EditorState.createEmpty()
      : EditorState.createWithContent(ContentState.createFromBlockArray(content.contentBlocks));
  };

  private blockRenderFunction = (block: ContentBlock): Nullable<ICustomBlock> => {
    if (block.getType() !== "atomic") {
      return null;
    }
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    const entityKey = block.getEntityAt(0);
    const entity = contentState.getEntity(entityKey);
    if (!entity || entity.getType() !== "IMAGE") {
      return null;
    }
    return {
      component: getImageComponent(this.editorHelpers),
      editable: false,
      props: {
        toggleReadOnly: this.toggleReadOnly,
      },
    };
  };

  private getEditorState = (): EditorState => this.state.editorState;

  private toggleReadOnly = (readOnly: boolean) => this.setState({ readOnly });
}
